import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import {
  Users, Package, Receipt, IndianRupee, TrendingUp, AlertTriangle,
  ArrowUpRight, FileText, Wrench, UserCheck, Play, PauseCircle,
  CheckCircle2, Truck, Archive,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SkeletonPage from "@/components/KovaSkeleton";
import EmptyState from "@/components/EmptyState";

/* --- KPI card (dark, neon accent) --- */
const KPI = ({ label, value, icon: Icon, testid, delta }) => (
  <div
    className="tzat-kpi group relative overflow-hidden hover:border-[#39FF14]/30 transition-colors"
    data-testid={testid}
  >
    <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#39FF14]/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-medium">{label}</div>
        <div className="mt-3 font-heading text-3xl font-bold text-[#F8F9FA] tracking-tight leading-none">{value}</div>
        {delta && (
          <div className="mt-2 text-[11px] text-[#39FF14] font-mono">{delta}</div>
        )}
      </div>
      <div className="w-10 h-10 rounded-[10px] bg-[#0B0F0C] border border-[#3A4640] flex items-center justify-center">
        <Icon size={16} className="text-[#39FF14]" />
      </div>
    </div>
  </div>
);

/* --- Service board pill card --- */
const ServiceStat = ({ label, value, icon: Icon, testid, filter }) => (
  <Link
    to={`/services?status=${filter}`}
    data-testid={testid}
    className="tzat-surface p-3 hover:border-[#39FF14]/40 transition-colors block"
  >
    <div className="flex items-center justify-between mb-2">
      <div className="w-7 h-7 rounded-[8px] bg-[#1A1F1C] border border-[#3A4640] flex items-center justify-center">
        <Icon size={12} className="text-[#39FF14]" />
      </div>
      <span className="font-heading text-2xl font-bold text-[#F8F9FA] tracking-tight">{value}</span>
    </div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-medium leading-tight">{label}</div>
  </Link>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [custMeta, setCustMeta] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/customers/meta").then((r) => setCustMeta(r.data)).catch(() => {});
  }, []);

  const applyRange = async () => {
    const { data } = await api.get(`/dashboard/range?from_date=${range.from}&to_date=${range.to ? range.to + "T23:59:59" : ""}`);
    setStats((s) => ({ ...s, ...data }));
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/sales.pdf?from_date=${range.from}&to_date=${range.to ? range.to + "T23:59:59" : ""}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = "sales_report.pdf";
    a.click();
  };

  if (!stats) return <SkeletonPage title kpi table rows={6} cols={5} />;

  const maxMonth = Math.max(1, ...stats.sales_by_month.map((s) => s.amount));

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      {/* Header row */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4 pb-6 border-b border-[#3A4640]">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="tzat-pulse" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">Overview</div>
          </div>
          <h1 className="font-heading text-3xl lg:text-[32px] font-bold text-[#F8F9FA] tracking-tight">Dashboard</h1>
          <p className="text-[#8A8F98] mt-1.5 text-sm">TZAT ERP at a glance — what needs your attention today.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading mb-1">From</div>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="dashboard-from-date" className="h-9 w-40 bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading mb-1">To</div>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="dashboard-to-date" className="h-9 w-40 bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]" />
          </div>
          <Button onClick={applyRange} data-testid="dashboard-apply-range" className="h-9 bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Apply</Button>
          <Button onClick={downloadPdf} data-testid="dashboard-pdf-btn" variant="outline" className="h-9 bg-transparent border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]">
            <FileText size={14} className="mr-1.5" /> PDF Report
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Total Revenue" value={formatCurrency(stats.total_revenue)} icon={IndianRupee} testid="stat-revenue" />
        <KPI label="Total Sales" value={stats.total_sales} icon={Receipt} testid="stat-sales" />
        <KPI label="Customers" value={stats.total_customers} icon={Users} testid="stat-customers" />
        <KPI label="Inventory Items" value={stats.total_products} icon={Package} testid="stat-inventory" />
      </div>

      {/* Service Overview */}
      {stats.service_stats && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">Service</div>
              <h2 className="font-heading text-lg font-semibold text-[#F8F9FA]">Workshop overview</h2>
            </div>
            <Link to="/services" className="text-xs text-[#8A8F98] hover:text-[#39FF14] flex items-center gap-1 transition-colors" data-testid="service-overview-link">
              Open service board <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <ServiceStat label="New Requests"      value={stats.service_stats.new}               icon={Wrench}       testid="svc-new"              filter="new" />
            <ServiceStat label="Assigned"          value={stats.service_stats.assigned}          icon={UserCheck}    testid="svc-assigned"         filter="assigned" />
            <ServiceStat label="In Progress"       value={stats.service_stats.in_progress}       icon={Play}         testid="svc-in-progress"      filter="in_progress" />
            <ServiceStat label="Waiting for Parts" value={stats.service_stats.waiting_for_parts} icon={PauseCircle}  testid="svc-waiting"          filter="waiting_for_parts" />
            <ServiceStat label="Ready for Pickup"  value={stats.service_stats.ready}             icon={CheckCircle2} testid="svc-ready"            filter="ready" />
            <ServiceStat label="Delivered Today"   value={stats.service_stats.delivered_today}   icon={Truck}        testid="svc-delivered-today"  filter="delivered" />
            <ServiceStat label="Closed Today"      value={stats.service_stats.closed_today}      icon={Archive}      testid="svc-closed-today"     filter="closed" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales chart */}
        <div className="lg:col-span-2 tzat-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#F8F9FA]">Revenue Trend</h3>
              <p className="text-xs text-[#8A8F98] mt-0.5">Sales by month</p>
            </div>
            <TrendingUp className="text-[#39FF14]" size={18} />
          </div>
          {stats.sales_by_month.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No sales yet"
              description="Your revenue chart will appear here as soon as the first invoice is recorded."
              variant="inline"
              testid="empty-sales-chart"
            />
          ) : (
            <div className="flex items-end gap-4 h-48">
              {stats.sales_by_month.map((s, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                  <div className="text-xs text-[#8A8F98] font-mono">{formatCurrency(s.amount).replace("₹", "")}</div>
                  <div
                    className="w-full bg-gradient-to-t from-[#2ECC71] to-[#39FF14] rounded-[4px] shadow-[0_0_12px_-4px_rgba(57,255,20,0.5)] group-hover:shadow-[0_0_18px_-2px_rgba(57,255,20,0.7)] transition-all"
                    style={{ height: `${(s.amount / maxMonth) * 140}px`, minHeight: "6px" }}
                  />
                  <div className="text-xs text-[#8A8F98] font-heading font-medium">{s.month}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending payments */}
        <div className="tzat-card p-6 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-[#39FF14]/10 rounded-full blur-2xl" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">Pending</div>
          <div className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#F8F9FA]">{formatCurrency(stats.pending_payments)}</div>
          <div className="text-[#8A8F98] text-sm mt-1">Outstanding payments</div>
          <Link to="/sales" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-[#39FF14] hover:text-[#F8F9FA] transition-colors">
            Review sales <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* Top customers */}
        <div className="tzat-card p-6">
          <h3 className="font-heading text-lg font-semibold text-[#F8F9FA] mb-4">Top Customers</h3>
          {stats.top_customers.length === 0 ? (
            <p className="text-sm text-[#8A8F98]">No sales yet.</p>
          ) : (
            <ul className="space-y-3">
              {stats.top_customers.map((c, i) => (
                <li key={i} className="flex items-center justify-between border-b border-[#3A4640] pb-3 last:border-0">
                  <div>
                    <div className="font-medium text-[#F8F9FA] text-sm">{c.name}</div>
                    <div className="text-xs text-[#8A8F98]">{c.village || c.phone}</div>
                  </div>
                  <div className="text-sm font-mono font-semibold text-[#39FF14]">{formatCurrency(c.total_purchases)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Customer Data Quality */}
        {custMeta && (
          <div className="tzat-card p-6" data-testid="dq-widget">
            <h3 className="font-heading text-lg font-semibold text-[#F8F9FA] mb-4">Customer Data Quality</h3>
            <div className="flex items-baseline gap-2 mb-3">
              <div className="font-heading text-4xl font-bold text-[#F8F9FA]" data-testid="dq-percentage">{custMeta.percentage}%</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">complete</div>
            </div>
            <div className="w-full bg-[#0B0F0C] rounded-full h-1.5 mb-4 overflow-hidden border border-[#3A4640]">
              <div className="h-1.5 bg-gradient-to-r from-[#2ECC71] to-[#39FF14] shadow-[0_0_10px_-2px_rgba(57,255,20,0.7)]" style={{ width: `${custMeta.percentage}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <Link to="/customers" className="tzat-surface p-2 hover:border-[#39FF14]/40">
                <div className="font-heading text-xl font-bold text-[#F8F9FA]">{custMeta.total}</div>
                <div className="text-[#8A8F98] text-[10px] uppercase tracking-wider">All</div>
              </Link>
              <Link to="/customers?incomplete=false" className="tzat-surface p-2 hover:border-[#39FF14]/40">
                <div className="font-heading text-xl font-bold text-[#39FF14]" data-testid="dq-complete">{custMeta.complete}</div>
                <div className="text-[#8A8F98] text-[10px] uppercase tracking-wider">Complete</div>
              </Link>
              <Link to="/customers?incomplete=true" className="tzat-surface p-2 hover:border-yellow-500/40">
                <div className="font-heading text-xl font-bold text-yellow-400" data-testid="dq-incomplete">{custMeta.incomplete}</div>
                <div className="text-[#8A8F98] text-[10px] uppercase tracking-wider">Incomplete</div>
              </Link>
            </div>
          </div>
        )}

        {/* Recent sales */}
        <div className="lg:col-span-2 tzat-card p-6">
          <h3 className="font-heading text-lg font-semibold text-[#F8F9FA] mb-4">Recent Sales</h3>
          {stats.recent_sales.length === 0 ? (
            <p className="text-sm text-[#8A8F98]">No sales yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading">
                <tr className="border-b border-[#3A4640]">
                  <th className="text-left py-2">Invoice</th>
                  <th className="text-left py-2">Customer</th>
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_sales.map((s, i) => (
                  <tr key={i} className="border-b border-[#3A4640] last:border-0 hover:bg-[#121815]/60 transition-colors">
                    <td className="py-3 font-mono text-xs text-[#F8F9FA]">{s.invoice_no}</td>
                    <td className="py-3 text-[#F8F9FA]">{s.customer?.name}</td>
                    <td className="py-3 text-[#8A8F98]">{s.product?.brand} {s.product?.model}</td>
                    <td className="py-3 text-right font-mono font-semibold text-[#39FF14]">{formatCurrency(s.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Low stock */}
        <div className="tzat-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-yellow-400" />
            <h3 className="font-heading text-lg font-semibold text-[#F8F9FA]">Low Stock</h3>
          </div>
          {stats.low_stock.length === 0 ? (
            <p className="text-sm text-[#8A8F98]">All good.</p>
          ) : (
            <ul className="space-y-3">
              {stats.low_stock.map((p) => (
                <li key={p.id} className="flex justify-between text-sm border-b border-[#3A4640] pb-2 last:border-0">
                  <span className="text-[#F8F9FA]">{p.brand} {p.model}</span>
                  <span className="font-mono text-red-400 font-semibold">{p.stock} left</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upcoming follow-ups */}
        <div className="lg:col-span-3 tzat-card p-6">
          <h3 className="font-heading text-lg font-semibold text-[#F8F9FA] mb-4">Upcoming Follow-ups</h3>
          {stats.upcoming_followups.length === 0 ? (
            <p className="text-sm text-[#8A8F98]">Nothing scheduled.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.upcoming_followups.map((f) => (
                <div key={f.id} className="tzat-surface p-3 hover:border-[#39FF14]/40 transition-colors">
                  <div className="text-[10px] uppercase text-[#39FF14] font-heading font-semibold mb-1 tracking-[0.15em]">{f.priority}</div>
                  <div className="font-medium text-[#F8F9FA] text-sm">{f.title}</div>
                  <div className="text-xs text-[#8A8F98] mt-1">{f.customer?.name} · Due {formatDate(f.due_date)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
